import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { biBlendMeasureKey } from '@euler/model/bi/blend';
import {
  BiDisplayFormat,
  BiDisplayFormatKind,
  BiFormatSpec,
  biColumnFormatWith,
  biDisplayFormatFitting,
  biDisplayFormatKindsFor,
  biFormatSpecText,
} from '@euler/model/bi/format';
import { BiMeasureKind, biMeasureKey } from '@euler/model/bi/query';
import { BiCell, BiQueryResult } from '@euler/model/bi/result';
import { BiEditorState, biEditorCardKeys } from './biEditorState';

/** One measure the 数字格式 section offers a format for. */
export interface BiEditorFormatTarget {
  key: string;
  name: string;
  /** How the column reads without the card's own format. */
  base: BiDisplayFormat;
  /** The format it reads in now: the card's own, else `base`. */
  current: BiDisplayFormat;
  /** Whether the card sets its own. */
  own: boolean;
  kinds: BiDisplayFormatKind[];
  /** The first value of the column in the preview, for the sample. */
  sample: BiCell;
}

/** The card's ratio measure keys, as its result names them. */
function ratioKeys(state: BiEditorState): Set<string> {
  const sides = state.blend
    ? [state.measures, state.blend.second.measures]
    : [state.measures];
  return new Set(
    sides.flatMap((measures, side) =>
      measures
        .filter(ref => ref.kind === BiMeasureKind.Ratio)
        .map(ref =>
          state.blend ? biBlendMeasureKey(side, ref) : biMeasureKey(ref),
        ),
    ),
  );
}

/**
 * The measures the card shows, each with the kinds that fit what it holds
 * (`biDisplayFormatKindsFor`). Read from the live preview, whose columns
 * carry the format the dataset gives them; none before it has run.
 */
export function biEditorFormatTargets(
  state: BiEditorState,
  result: BiQueryResult | null,
): BiEditorFormatTarget[] {
  if (!result) return [];
  const ratios = ratioKeys(state);
  return biEditorCardKeys(state).measures.flatMap(key => {
    const index = result.columns.findIndex(column => column.key === key);
    if (index < 0) return [];
    const column = result.columns[index];
    const base = biColumnFormatWith(column, undefined)!;
    const own: BiFormatSpec | undefined = state.formats?.[key];
    return [
      {
        key,
        name: biText(column.label),
        base,
        current: biColumnFormatWith(column, own)!,
        own: own !== undefined,
        kinds: biDisplayFormatKindsFor(base, ratios.has(key)),
        sample: result.rows.find(row => row[index] !== null)?.[index] ?? null,
      },
    ];
  });
}

/**
 * The state with a measure's own format set, or removed (null, or the
 * same as the column's own, so nothing is saved that changes nothing).
 */
export function biEditorWithFormat(
  state: BiEditorState,
  key: string,
  edited: BiDisplayFormat | null,
  base?: BiDisplayFormat,
): BiEditorState {
  const trimmed = edited
    ? biDisplayFormatFitting({
        ...edited,
        unit: edited.unit?.trim() || undefined,
      })
    : null;
  const format =
    trimmed && !(base && biFormatSpecText(trimmed) === biFormatSpecText(base))
      ? trimmed
      : null;
  const rest = Object.fromEntries(
    Object.entries(state.formats ?? {}).filter(([other]) => other !== key),
  );
  const formats = format ? { ...rest, [key]: format } : rest;
  const next: BiEditorState = { ...state, formats };
  if (!Object.keys(formats).length) delete next.formats;
  return next;
}
